import type { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import {
  collectDexieSnapshot,
  planDexieToSqliteMigration,
  validateSnapshotForSqlite,
} from '@infra/persistence/Migrations/dexie_to_sqlite';
import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';
import { BackupService } from '@infra/backup/BackupService';
import { logger } from '@infra/telemetry/Logger';
import { isLocalOfflineMode } from '@infra/licence/DataModeResolver';
import type { DataMode } from '@infra/licence/ActivationClaims';

export interface CutoverResult {
  migrated: boolean;
  skipped: boolean;
  reason?: string;
  counts?: Record<string, number>;
}

/**
 * Migration Dexie → SQLite chiffré (processus principal).
 * Conserve Dexie en secours si l'import échoue.
 */
export class DexieToSqliteCutoverService {
  static async maybeMigrate(
    db: NexxusDatabase,
    dataMode: DataMode | null,
    tenantId?: string,
  ): Promise<CutoverResult> {
    if (!dataMode || !isLocalOfflineMode(dataMode)) {
      return { migrated: false, skipped: true, reason: 'mode non local' };
    }
    if (!SqliteEntityBridge.isAvailable()) {
      return { migrated: false, skipped: true, reason: 'hors Electron' };
    }
    if (await SqliteEntityBridge.isSqlitePrimary()) {
      return { migrated: false, skipped: true, reason: 'déjà sur SQLite' };
    }

    const snap = await collectDexieSnapshot(db);
    const total = snap.reduce((n, t) => n + t.rows.length, 0);
    if (total === 0) {
      const emptyPlan = planDexieToSqliteMigration(snap);
      if (!emptyPlan.ready) {
        return { migrated: false, skipped: true, reason: 'snapshot vide invalide' };
      }
      const imported = await SqliteEntityBridge.importSnapshot(snap);
      if (!imported.ok) {
        return { migrated: false, skipped: false, reason: imported.error ?? 'import échoué' };
      }
      logger.info({ event: 'sqlite_cutover_empty_db' });
      return { migrated: true, skipped: false, counts: imported.counts };
    }

    const blockers = validateSnapshotForSqlite(snap);
    if (blockers.length > 0) {
      logger.warn({ event: 'sqlite_cutover_blocked', blockers });
      return { migrated: false, skipped: false, reason: blockers.join('; ') };
    }

    if (tenantId) {
      try {
        const backup = new BackupService(db);
        await backup.export(tenantId);
        logger.info({ event: 'sqlite_cutover_pre_backup' });
      } catch (err) {
        logger.error({ event: 'sqlite_cutover_backup_failed', error: String(err) });
        return { migrated: false, skipped: false, reason: 'sauvegarde pré-migration impossible' };
      }
    }

    const plan = planDexieToSqliteMigration(snap);
    const imported = await SqliteEntityBridge.importSnapshot(snap);
    if (!imported.ok) {
      return { migrated: false, skipped: false, reason: imported.error ?? 'import échoué' };
    }

    for (const table of plan.tables) {
      const expected = table.rowCount;
      const actual = imported.counts?.[table.name] ?? 0;
      if (expected !== actual) {
        logger.error({
          event: 'sqlite_cutover_count_mismatch',
          table: table.name,
          expected,
          actual,
        });
        return {
          migrated: false,
          skipped: false,
          reason: `comptage ${table.name}: attendu ${expected}, obtenu ${actual}`,
        };
      }
    }

    logger.info({ event: 'sqlite_cutover_done', counts: imported.counts });
    return { migrated: true, skipped: false, counts: imported.counts };
  }
}
